import { useLayoutEffect, useMemo, useState, useRef } from "react";
import Moveable from "react-moveable";
import { ObjectActionsType, useObjectsDispatch, useObjectsState } from "../../contexts/ObjectsProvider";
import { useTimeline } from "../../contexts/TimelineProvider/TimelineProvider";
import {
  SelectedObjectActionTypes,
  useSelectedObjectDispatch,
  useSelectedObjectState,
} from "../../contexts/SelectedObjectProvider/SelectedObjectProvider";
import { createLogger, getSVGPadding } from "../../utils";
import { useMovableElementsPlaneState } from "../../contexts/MovableElementsPlaneProvider";
import CropRotatable from "./lib/ables/CropRotatable";
import CropResizable from "./lib/ables/CropResizable";
import { useMiscUI } from "../../contexts/MiscUI/MiscUIProvider";
import { OBJECT_TYPE_PANORAMIC } from "../../const";
import { useLessonPagesState } from "../../contexts/LessonPagesProvider/LessonPagesProvider";
import { percentageToValue, valueToPercentage } from "../../utils/Conversion";
import { useObjectPosition } from "../../hooks/useObjectPosition";

const log = createLogger("AbstractMoveable", { color: "blue" });
const targetIdMap = new WeakMap();
const SNAP_OVERLAP_PX = 10; // Number of pixels to overlap when a shape is resized

// Helper to get objectId from target element using dataset
const getTargetObjectId = (target: Element): string | undefined => {
  const htmlEl = target as HTMLElement;
  return htmlEl.dataset.objectid || htmlEl.dataset.ref || undefined;
};

export function AbstractMoveable({ pageDims }: any) {
  const objectsState = useObjectsState();
  const selectedObjectState = useSelectedObjectState();
  const { viewportDOMElementHeight, viewportDOMElementWidth } = useMovableElementsPlaneState();
  const objectsDispatch = useObjectsDispatch();
  const selectedObjectDispatch = useSelectedObjectDispatch();
  const isCropping = objectsState.isCropping;
  const selectedObject = objectsState.selectedObjects[0];
  const [localKeepAspectRatio, setLocalKeepAspectRatio] = useState<boolean | null>(null);
  const lastSnapDataRef = useRef<{ guidelines: any[]; elements: any[]; gaps: any[] } | null>(null);
  const {
    rotation: finalRotation,
    position: [framedX, framedY],
    size: [framedWidth, framedHeight],
  } = useObjectPosition(
    selectedObject?.objectId,
    selectedObject?.top,
    selectedObject?.left,
    selectedObject?.width,
    selectedObject?.height,
    selectedObject?.rotation,
    selectedObject?.opacity,
  );
  const [tl] = useTimeline();
  const [miscUI] = useMiscUI();
  const snapToGrid = miscUI.snapToGrid;
  const moveableRef = useRef<any>(null);
  const { selectedPanel } = useLessonPagesState();
  const isMaskingShape = objectsState.selectedObjects[0]?.type === "shape";
  const isFreeFormPoly = objectsState.selectedObjects[0]?.type === "freeFormPoly";
  const isSmartObject = objectsState.selectedObjects[0]?.type === "smartObject";
  // mapping objects, expensive. consider memoizing
  const guides = objectsState.objectList.map((object) => {
    return `[data-objectid="${object.objectId}"]`;
  });
  const hasClipCoordinates = (object: any) => {
    if (object && ("clipPath" in object || "clipPathString" in object)) {
      if (object.clipPathString) {
        return true;
      } else if (object.clipPath) {
        return !Object.values(object.clipPath).every((value) => value === 0);
      }
    }
    return false;
  };

  const isCroppedImage = hasClipCoordinates(objectsState.selectedObjects[0]);

  const isMultipleSelection = objectsState.selectedObjects.length > 1;
  const selected = objectsState.selectedObjects.map((object) => {
    // Use data-ref for cropped smart objects in multi-select and not cropping
    if (isMultipleSelection && !isCropping) {
      const isThisObjectCropped = hasClipCoordinates(object);
      const isThisObjectSmartObject = object.type === "smartObject";

      if (isThisObjectSmartObject && isThisObjectCropped) {
        return `[data-ref="${object.objectId}"]`;
      }
    }

    // Use data-ref for objects being actively cropped
    if (isCroppedImage && isCropping) {
      return `[data-ref="${object.objectId}"]`;
    }

    // Default selector
    return `[data-objectid="${object.objectId}"]`;
  });

  const hasRotation = useMemo(() => {
    if (objectsState.selectedObjects.length > 0) {
      if (
        objectsState.selectedObjects[0]?.type &&
        // TODO: rollback adding the "video", "table", "SCORM" types
        [OBJECT_TYPE_PANORAMIC].includes(objectsState.selectedObjects[0]?.type)
      ) {
        return false;
      }
    }
    return true;
  }, [objectsState]);

  const determineAnimatedObjectOutOfBoundaries = (objectId: string) => {
    const animatedObject = objectsState.animatedObjects.find((object) => object.id === objectId);
    if (animatedObject) {
      return (
        tl.scrubbingCurrentTime < animatedObject.start ||
        tl.scrubbingCurrentTime > (animatedObject.end ?? tl.sequenceLength)
      );
    }
  };

  useLayoutEffect(() => {
    if (moveableRef.current) {
      moveableRef.current.updateRect();
    }
  });

  const className = (isDisplayed: boolean, grayBorderIsOn: boolean) => {
    const selectedObjectType = objectsState.selectedObjects[0] ? objectsState.selectedObjects[0]?.type : null;
    return [
      `${typeof isDisplayed === "undefined" || isDisplayed ? "" : "target-not-displayed"} ${grayBorderIsOn}`,
      selectedObjectType === OBJECT_TYPE_PANORAMIC && miscUI.objectLocked ? "panoramic-moveable" : "",
      selectedObjectType === "smartObject" && selectedPanel === "advanced" ? "smartobject-moveable" : "",
      selectedObjectType === "freeFormPoly" && selectedPanel === "advanced" ? "advanced-moveable" : "",
    ].join(" ");
  };
  const getRenderDirections = () => {
    if (
      objectsState.selectedObjects[0]?.type?.includes("Arrow") ||
      objectsState.selectedObjects[0]?.type?.includes("Line")
    ) {
      return ["e", "w"];
    }

    if (isCroppedImage) {
      return ["nw", "n", "ne", "e", "sw", "s", "se", "w"];
    }
    return ["nw", "ne", "sw", "se"];
  };

  const canResize = () => {
    if (isCroppedImage && isCropping) {
      return true;
    }
    if (isCroppedImage) {
      return true;
    }
    return true;
  };

  const getLockAspectRatio = () => {
    if (isCropping && localKeepAspectRatio === false) {
      return false;
    }
    return objectsState.selectedObjects[0]?.lockAspectRatio ?? false;
  };

  const renderDirections = getRenderDirections();
  return (
    <Moveable
      target={selected}
      ref={(ref) => {
        moveableRef.current = ref;
        if (ref) {
          objectsDispatch({
            type: ObjectActionsType.SET_MOVEABLE_OBJECT_REF,
            moveableRef: ref,
          });
        }
      }}
      renderDirections={renderDirections}
      checkInput
      resizable={canResize()}
      throttleResize={0}
      draggable={!miscUI.objectLocked && !isCropping}
      scalable={!miscUI.objectLocked}
      rotatable={hasRotation && !miscUI.objectLocked}
      snappable={snapToGrid}
      elementGuidelines={guides}
      triggerAblesSimultaneously={false}
      clipTargetBounds={true}
      clippable={isCropping}
      cropresizable={isCroppedImage}
      cropscalable={isCroppedImage}
      ables={isCroppedImage ? [CropResizable, CropRotatable] : []}
      props={{
        cropresizable: isCroppedImage,
      }}
      clipRelative={false}
      clipArea={false}
      dragWithClip={true}
      defaultClipPath={"inset"}
      keepRatio={getLockAspectRatio()}
      originRelative={true}
      // originDraggable={true}
      stopPropagation={true}
      className={className(true, false)}
      onRenderStart={({ inputEvent }) => {
        if (inputEvent) {
          inputEvent.stopPropagation();
        }
      }}
      onRotateStart={({ target, clientX, clientY, datas }) => {
        if (isSmartObject && isCroppedImage) {
          datas.originalElementWhenCropped = document.querySelector(`[data-ref="${selectedObject.objectId}"]`);
          const {
            left: parentLeft,
            top: parentTop,
            width: parentWidth,
            height: parentHeight,
          } = datas.originalElementWhenCropped?.getBoundingClientRect();
          const {
            left: childLeft,
            top: childTop,
            width: childWidth,
            height: childHeight,
          } = target.getBoundingClientRect();

          const originX = (childLeft - parentLeft + childWidth / 2) / parentWidth;
          const originY = (childTop - parentTop + childHeight / 2) / parentHeight;
          if (!selectedObject.transformOrigin && (selectedObject.rotation ?? 0) === 0) {
            datas.originalElementWhenCropped.style.transformOrigin = `${originX * 100}% ${originY * 100}%`;
            objectsDispatch({
              type: ObjectActionsType.UPDATE_OBJECT,
              payload: {
                objectId: selectedObject.objectId,
                object: {
                  transformOrigin: { x: originX * 100, y: originY * 100 },
                },
              },
            });
          }
        }
      }}
      onRotate={({ target, rotate, transform, datas, delta, ...rest }) => {
        if (isSmartObject && isCroppedImage) {
          const newRotation = delta + (finalRotation ?? 0);
          console.log("newRotation", newRotation);
          objectsDispatch({
            type: ObjectActionsType.UPDATE_OBJECT_AT_TIME_FROM_MOVEMENT,
            payload: {
              time: tl.scrubbingCurrentTime,
              rotation: newRotation,
            },
          });
          selectedObjectDispatch({
            type: SelectedObjectActionTypes.SET_ROTATION,
            payload: newRotation,
          });
          datas.rotation = newRotation;
        } else {
          datas.rotation = rotate;
          target.style.transform = transform;
          selectedObjectDispatch({
            type: SelectedObjectActionTypes.SET_ROTATION,
            payload: rotate,
          });
        }
      }}
      onRotateEnd={({ datas, target }) => {
        if (isSmartObject && isCroppedImage) {
          return;
        }

        const outOfTimeBoundaries = determineAnimatedObjectOutOfBoundaries(objectsState.selectedObjects[0].objectId);
        if (outOfTimeBoundaries) {
          return (target.style.transform = datas.initialTransform);
        }
        if (typeof datas.rotation === "number") {
          objectsDispatch({
            type: ObjectActionsType.UPDATE_OBJECT_AT_TIME_FROM_MOVEMENT,
            payload: {
              time: tl.scrubbingCurrentTime,
              rotation: datas.rotation,
            },
          });
        }
      }}
      onResizeStart={({ target, datas }) => {
        datas.initialTransform = target.style.transform;
        datas.width = target.clientWidth;
        datas.height = target.clientHeight;
        datas.initialWidth = target.clientWidth;
        datas.initialHeight = target.clientHeight;

        if (isFreeFormPoly) {
          const ffAnnotation = objectsState.selectedObjects[0];
          const points = ffAnnotation.points;
          const padding = getSVGPadding(ffAnnotation.strokeWidth, datas.width, datas.height);
          const percentPoints = points.map((point) => {
            return {
              ...point,
              xPercent: ((point.x - padding) / (datas.width - 2 * padding)) * 100,
              yPercent: ((point.y - padding) / (datas.height - 2 * padding)) * 100,
            };
          });
          datas.percentPoints = percentPoints;
        }

        if (isSmartObject && isCroppedImage && !isCropping) {
          datas.originalElementWhenCropped = document.querySelector(`[data-ref="${selectedObject.objectId}"]`);
          const {
            left: childLeft,
            top: childTop,
            width: childWidth,
            height: childHeight,
          } = target.getBoundingClientRect();
          console.log("childLeft", childLeft);
          datas.startingChildLeft = childLeft;
          datas.startingChildTop = childTop;
          datas.startingChildWidth = childWidth;
          datas.startingChildHeight = childHeight;
          datas.startingWidth = framedWidth;
          datas.startingHeight = framedHeight;
          datas.xAcc = 0;
          datas.yAcc = 0;
          datas.isPortrait = framedHeight > framedWidth;
          datas.ratio = framedHeight / framedWidth;
          datas.startingX = framedX;
          datas.startingY = framedY;
          datas.newFramedX = framedX;
          datas.newFramedY = framedY;
        } else {
          datas.initialTransform = target.style.transform;
          datas.width = target.clientWidth;
          datas.height = target.clientHeight;
        }
      }}
      onResize={({ target, width: newWidth, height: newHeight, drag, datas }) => {
        if (selectedPanel === "advanced" && !isFreeFormPoly) return null;
        if (isFreeFormPoly) {
          if (newWidth < 50 || newHeight < 50) {
            return;
          }
        }

        if (isSmartObject && isCroppedImage && !isCropping) {
          // get the new dimensions of the faux div
          console.log("newWidth", newWidth, "newHeight", newHeight);
          const leftPercentage = selectedObject.clipPath.left;
          const rightPercentage = selectedObject.clipPath.right;
          const topPercentage = selectedObject.clipPath.top;
          const bottomPercentage = selectedObject.clipPath.bottom;
          const { left: childLeft, top: childTop } = target.getBoundingClientRect();
          const childXMovement = childLeft - datas.startingChildLeft;
          const childYMovement = childTop - datas.startingChildTop;
          console.log("childLeft", childLeft, "childTop", childTop);
          console.log(
            "datas.startingChildLeft",
            datas.startingChildLeft,
            "datas.startingChildTop",
            datas.startingChildTop,
          );
          console.log("childXMovement", childXMovement, "childYMovement", childYMovement);
          const clipWidthPercentage = 100 - leftPercentage - rightPercentage;
          const clipHeightPercentage = 100 - topPercentage - bottomPercentage;
          const newParentWidth = newWidth / (clipWidthPercentage / 100);
          const newParentHeight = newHeight / (clipHeightPercentage / 100);
          console.log("W", newParentWidth, "H", newParentHeight);
          const percentWidth = valueToPercentage(newParentWidth, viewportDOMElementWidth || 0);
          const percentHeight = valueToPercentage(newParentHeight, viewportDOMElementHeight || 0);

          const childXMovementPercentage = valueToPercentage(childXMovement, viewportDOMElementWidth || 0);
          const childYMovementPercentage = valueToPercentage(childYMovement, viewportDOMElementHeight || 0);

          const normalizedXMovement = percentageToValue(childXMovementPercentage, viewportDOMElementWidth || 0);
          const normalizedYMovement = percentageToValue(childYMovementPercentage, viewportDOMElementHeight || 0);

          console.log("normalizedXMovement", normalizedXMovement, "normalizedYMovement", normalizedYMovement);
          const newX = framedX - normalizedXMovement;
          const newY = framedY - normalizedYMovement;
          datas.newFramedX = newX;
          datas.newFramedY = newY;

          objectsDispatch({
            type: ObjectActionsType.UPDATE_OBJECT_AT_TIME_FROM_MOVEMENT,
            payload: {
              time: tl.scrubbingCurrentTime,
              width: percentWidth,
              height: percentHeight,
              x: newX,
              y: newY,
            },
          });
        } else {
          target.style.width = `${newWidth}px`;
          target.style.height = `${newHeight}px`;
          target.style.transform = drag.transform;
          const beforeTranslate = drag.beforeTranslate;
          const oldWidth = datas.width;
          const oldHeight = datas.height;
          datas.left = beforeTranslate[0];
          datas.top = beforeTranslate[1];
          datas.width = newWidth;
          datas.height = newHeight;
          selectedObjectDispatch({
            type: SelectedObjectActionTypes.SET_X,
            payload: beforeTranslate[0],
          });
          selectedObjectDispatch({
            type: SelectedObjectActionTypes.SET_Y,
            payload: beforeTranslate[1],
          });
          selectedObjectDispatch({
            type: SelectedObjectActionTypes.SET_WIDTH,
            payload: newWidth,
          });
          selectedObjectDispatch({
            type: SelectedObjectActionTypes.SET_HEIGHT,
            payload: newHeight,
          });
        }

        /**
         * LEGACY STUFF
         */
        /**
         * Triangle
         */
        if (target.firstChild?.nodeName === "polygon" && target.getAttribute("name") === "triangle") {
          target.firstChild.attributes.points.nodeValue = `4,${target.clientHeight - 4} ${
            (target.clientWidth - 2) / 2
          },4 ${target.clientWidth - 4},${target.clientHeight - 4}`;
        }
        /**
         * Ellipse
         */
        if ((target as any).firstChild.nodeName === "ellipse") {
          (target as any).firstChild.attributes.cx.nodeValue = `${target.clientWidth / 2}`;
          (target as any).firstChild.attributes.cy.nodeValue = `${target.clientHeight / 2}`;
          (target as any).firstChild.attributes.rx.nodeValue = `${target.clientWidth / 2 - 3}`;
          (target as any).firstChild.attributes.ry.nodeValue = `${target.clientHeight / 2 - 3}`;
        }
      }}
      onResizeEnd={({ datas, target }) => {
        if (isSmartObject && isCroppedImage && !isCropping) {
          return;
        }

        const pixelX = datas.left;
        const pixelY = datas.top;
        const pixelWidth = datas.width;
        const pixelHeight = datas.height;

        // Apply snap adjustments for masking shapes after resize ends
        if (isMaskingShape && lastSnapDataRef.current) {
          const { guidelines, elements, gaps } = lastSnapDataRef.current;

          if (guidelines?.length || elements?.length || gaps?.length) {
            const currentObject = objectsState.selectedObjects[0];
            if (currentObject && viewportDOMElementWidth && viewportDOMElementHeight) {
              const left = currentObject.left ?? 0;
              const top = currentObject.top ?? 0;
              const width = currentObject.width ?? 0;
              const height = currentObject.height ?? 0;

              try {
                const pixelX = percentageToValue(left, viewportDOMElementWidth);
                const pixelY = percentageToValue(top, viewportDOMElementHeight);
                const pixelWidth = percentageToValue(width, viewportDOMElementWidth);
                const pixelHeight = percentageToValue(height, viewportDOMElementHeight);

                let snapTop = false;
                let snapBottom = false;
                let snapLeft = false;
                let snapRight = false;

                const allSnaps = [...(elements || []), ...(guidelines || []), ...(gaps || [])];

                allSnaps.forEach((snap) => {
                  if (snap.type === "horizontal") {
                    const guidelineY = snap.pos[1];
                    const objectTop = pixelY;
                    const objectBottom = pixelY + pixelHeight;

                    if (Math.abs(guidelineY - objectTop) < 5) {
                      snapTop = true;
                    }
                    if (Math.abs(guidelineY - objectBottom) < 5) {
                      snapBottom = true;
                    }
                  } else if (snap.type === "vertical") {
                    const guidelineX = snap.pos[0];
                    const objectLeft = pixelX;
                    const objectRight = pixelX + pixelWidth;

                    if (Math.abs(guidelineX - objectLeft) < 5) {
                      snapLeft = true;
                    }
                    if (Math.abs(guidelineX - objectRight) < 5) {
                      snapRight = true;
                    }
                  }
                });

                let adjustedWidth = pixelWidth;
                let adjustedHeight = pixelHeight;
                let adjustedX = pixelX;
                let adjustedY = pixelY;

                if (snapTop || snapBottom) {
                  adjustedHeight += SNAP_OVERLAP_PX;
                  adjustedY -= SNAP_OVERLAP_PX / 2;
                }

                if (snapLeft || snapRight) {
                  adjustedWidth += SNAP_OVERLAP_PX;
                  adjustedX -= SNAP_OVERLAP_PX / 2;
                }

                // Only apply adjustments if we detected any snapping
                if (snapTop || snapBottom || snapLeft || snapRight) {
                  selectedObjectDispatch({
                    type: SelectedObjectActionTypes.SET_X,
                    payload: adjustedX,
                  });

                  selectedObjectDispatch({
                    type: SelectedObjectActionTypes.SET_Y,
                    payload: adjustedY,
                  });

                  selectedObjectDispatch({
                    type: SelectedObjectActionTypes.SET_WIDTH,
                    payload: adjustedWidth,
                  });

                  selectedObjectDispatch({
                    type: SelectedObjectActionTypes.SET_HEIGHT,
                    payload: adjustedHeight,
                  });

                  // Update the actual object state for canvas rendering
                  const percentWidth = valueToPercentage(adjustedWidth, viewportDOMElementWidth);
                  const percentHeight = valueToPercentage(adjustedHeight, viewportDOMElementHeight);

                  objectsDispatch({
                    type: ObjectActionsType.UPDATE_OBJECT_AT_TIME_FROM_MOVEMENT,
                    payload: {
                      time: tl.scrubbingCurrentTime,
                      x: adjustedX,
                      y: adjustedY,
                      width: percentWidth,
                      height: percentHeight,
                    },
                  });
                }

                // Clear the snap data after processing
                lastSnapDataRef.current = null;
              } catch (error) {
                console.error("Error in onResizeEnd snap handler:", error);
              }
            }
          }
        }

        const outOfTimeBoundaries = determineAnimatedObjectOutOfBoundaries(objectsState.selectedObjects[0].objectId);
        if (outOfTimeBoundaries) {
          return (target.style.transform = datas.initialTransform);
        }
        if (typeof pixelX === "number") {
          objectsDispatch({
            type: ObjectActionsType.UPDATE_OBJECT_AT_TIME_FROM_MOVEMENT,
            payload: {
              time: tl.scrubbingCurrentTime,
              x: pixelX,
            },
          });
        }
        log("pixelY", pixelY, "datas.deltaY", datas.deltaY);
        if (typeof pixelY === "number") {
          objectsDispatch({
            type: ObjectActionsType.UPDATE_OBJECT_AT_TIME_FROM_MOVEMENT,
            payload: {
              time: tl.scrubbingCurrentTime,
              y: pixelY,
            },
          });
        }
        if (typeof pixelWidth === "number") {
          const percentWidth = (pixelWidth / (viewportDOMElementWidth ?? 1)) * 100;
          objectsDispatch({
            type: ObjectActionsType.UPDATE_OBJECT_AT_TIME_FROM_MOVEMENT,
            payload: {
              time: tl.scrubbingCurrentTime,
              width: percentWidth,
            },
          });
        }
        if (typeof pixelHeight === "number") {
          const percentHeight = (pixelHeight / (viewportDOMElementHeight ?? 1)) * 100;
          objectsDispatch({
            type: ObjectActionsType.UPDATE_OBJECT_AT_TIME_FROM_MOVEMENT,
            payload: {
              time: tl.scrubbingCurrentTime,
              height: percentHeight,
            },
          });
        }

        /**
         * Resize the blurCutoutShapes as the object is resized
         */

        const blurCutoutShapes = selectedObjectState.blurCutoutShapes;
        if (blurCutoutShapes && blurCutoutShapes.length > 0) {
          const horizontalDelta = datas.width / datas.initialWidth;
          const verticalDelta = datas.height / datas.initialHeight;

          const newBlurCutoutShapes = blurCutoutShapes.map((blurCutoutShape) => ({
            ...blurCutoutShape,
            x: blurCutoutShape.x * horizontalDelta,
            y: blurCutoutShape.y * verticalDelta,
            w: blurCutoutShape.w * horizontalDelta,
            h: blurCutoutShape.h * verticalDelta,
          }));

          selectedObjectDispatch({
            type: SelectedObjectActionTypes.SET_BLUR_CUTOUT,
            payload: newBlurCutoutShapes,
          });

          objectsDispatch({
            type: ObjectActionsType.UPDATE_OBJECT_AT_TIME_FROM_MOVEMENT,
            payload: {
              time: tl.scrubbingCurrentTime,
              blurCutoutShapes: newBlurCutoutShapes,
            },
          });
        }
      }}
      onDragStart={({ target, datas, clientX, clientY }) => {
        if (isSmartObject && isCroppedImage) {
          datas.xAcc = 0;
          datas.yAcc = 0;
          datas.objectId = selectedObject.objectId;
          datas.originalElementWhenCropped = document.querySelector(`[data-ref="${selectedObject.objectId}"]`);
          datas.initialTransform = datas.originalElementWhenCropped?.style.transform || "";

          // Store the initial position from framedX and framedY
          datas.startingX = framedX;
          datas.startingY = framedY;

          // Store initial values for checking if position actually changed
          datas.initialX = framedX;
          datas.initialY = framedY;
        } else {
          datas.initialTransform = target.style.transform;
          datas.deltaX = 0;
          datas.deltaY = 0;
        }
      }}
      onDrag={({ target, datas, beforeTranslate, transform, delta, inputEvent }) => {
        if (isSmartObject && isCroppedImage) {
          const movementX = inputEvent.movementX;
          datas.xAcc += movementX;
          const movementY = inputEvent.movementY;
          datas.yAcc += movementY;
          const pixelX = datas.startingX + datas.xAcc;
          // we have to manually generate the transform string becasue we target a different element
          if (typeof pixelX === "number") {
            objectsDispatch({
              type: ObjectActionsType.UPDATE_OBJECT_AT_TIME_FROM_MOVEMENT,
              payload: {
                time: tl.scrubbingCurrentTime,
                x: pixelX,
              },
            });

            // Update the selected object state as well
            selectedObjectDispatch({
              type: SelectedObjectActionTypes.SET_X,
              payload: pixelX,
            });
          }

          const pixelY = datas.startingY + datas.yAcc;
          if (typeof pixelY === "number") {
            objectsDispatch({
              type: ObjectActionsType.UPDATE_OBJECT_AT_TIME_FROM_MOVEMENT,
              payload: {
                time: tl.scrubbingCurrentTime,
                y: pixelY,
              },
            });

            // Update the selected object state as well
            selectedObjectDispatch({
              type: SelectedObjectActionTypes.SET_Y,
              payload: pixelY,
            });
          }
        } else {
          datas.deltaX += delta[0];
          datas.deltaY += delta[1];

          const adjustedLeft = beforeTranslate[0];
          const adjustedTop = beforeTranslate[1];

          target.style.transform = `translate(${adjustedLeft}px, ${adjustedTop}px)`;
          datas.left = adjustedLeft;
          datas.top = adjustedTop;

          selectedObjectDispatch({
            type: SelectedObjectActionTypes.SET_X,
            payload: adjustedLeft,
          });
          selectedObjectDispatch({
            type: SelectedObjectActionTypes.SET_Y,
            payload: adjustedTop,
          });
          objectsDispatch({
            type: ObjectActionsType.UPDATE_OBJECT_AT_TIME_FROM_MOVEMENT,
            payload: {
              time: tl.scrubbingCurrentTime,
              x: adjustedLeft,
              y: adjustedTop,
            },
          });
        }
      }}
      onDragEnd={({ target, datas }) => {
        const outOfTimeBoundaries = determineAnimatedObjectOutOfBoundaries(objectsState.selectedObjects[0].objectId);

        if (isSmartObject && isCroppedImage) {
          if (outOfTimeBoundaries) {
            // Reset to initial position if out of time boundaries
            if (datas.originalElementWhenCropped) {
              datas.originalElementWhenCropped.style.transform = datas.initialTransform;
            }
            return;
          }

          // For cropped Smart Objects, ensure the final position is updated
          const pixelX = datas.startingX + datas.xAcc;
          const pixelY = datas.startingY + datas.yAcc;

          // Only update if position has actually changed
          const positionChanged =
            (typeof pixelX === "number" && Math.abs(pixelX - datas.initialX) > 0.1) ||
            (typeof pixelY === "number" && Math.abs(pixelY - datas.initialY) > 0.1);

          if (positionChanged) {
            objectsDispatch({
              type: ObjectActionsType.UPDATE_OBJECT_AT_TIME_FROM_MOVEMENT,
              payload: {
                time: tl.scrubbingCurrentTime,
                x: pixelX,
                y: pixelY,
                objectId: datas.objectId || selectedObject.objectId,
              },
            });
          }
          return;
        }

        const pixelX = datas.left;
        const pixelY = datas.top;

        if (outOfTimeBoundaries) {
          return (target.style.transform = datas.initialTransform);
        }

        const o = {};
        if (typeof pixelY === "number" && datas.deltaY !== 0) {
          o.y = pixelY;
        }
        if (typeof pixelX === "number" && datas.deltaX !== 0) {
          o.x = pixelX;
        }

        objectsDispatch({
          type: ObjectActionsType.UPDATE_OBJECT_AT_TIME_FROM_MOVEMENT,
          payload: {
            time: tl.scrubbingCurrentTime,
            ...o,
          },
        });
      }}
      onDragGroupStart={({ inputEvent, targets }) => {
        inputEvent.stopPropagation();
        targets.forEach((target) => {
          const objectId = getTargetObjectId(target);

          if (!objectId) {
            targetIdMap.set(target, {
              initialTransform: target.style.transform,
              isCroppedSmartObject: false,
            });
            return;
          }

          // Check if this target is a cropped smart object
          const selectedObj = objectsState.objectList.find((obj) => obj.objectId === objectId);
          const isCroppedSmartObject = selectedObj?.type === "smartObject" && hasClipCoordinates(selectedObj);

          if (isCroppedSmartObject) {
            const computedStyle = globalThis.getComputedStyle(target);
            const matrix = new DOMMatrixReadOnly(computedStyle.transform);
            const currentX = matrix.m41; // translateX
            const currentY = matrix.m42; // translateY

            targetIdMap.set(target, {
              initialTransform: target.style.transform,
              isCroppedSmartObject: true,
              objectId: objectId,
              startingX: currentX,
              startingY: currentY,
              xAcc: 0,
              yAcc: 0,
            });
          } else {
            targetIdMap.set(target, {
              initialTransform: target.style.transform,
              isCroppedSmartObject: false,
            });
          }
        });
      }}
      onDragGroup={({ events }) => {
        events.forEach(({ target, beforeTranslate, transform, delta }) => {
          const data = targetIdMap.get(target);

          if (data.isCroppedSmartObject) {
            // For cropped smart objects, accumulate movement
            data.xAcc += delta[0];
            data.yAcc += delta[1];

            const pixelX = data.startingX + data.xAcc;
            const pixelY = data.startingY + data.yAcc;

            data.left = pixelX;
            data.top = pixelY;

            // Update state immediately for cropped smart objects
            objectsDispatch({
              type: ObjectActionsType.UPDATE_OBJECT_AT_TIME_FROM_MOVEMENT,
              payload: {
                time: tl.scrubbingCurrentTime,
                x: pixelX,
                y: pixelY,
                objectId: data.objectId,
              },
            });
          } else {
            // For regular objects, use beforeTranslate
            data.left = beforeTranslate[0];
            data.top = beforeTranslate[1];
            target.style.transform = transform;
          }
        });
      }}
      onDragGroupEnd={({ targets }) => {
        targets.forEach((target) => {
          const data = targetIdMap.get(target);
          const { left, top, initialTransform } = data;

          const objectId = getTargetObjectId(target);
          if (!objectId) return;

          const isOutOfTimeBoundaries = determineAnimatedObjectOutOfBoundaries(objectId);
          if (isOutOfTimeBoundaries) {
            return (target.style.transform = initialTransform);
          }

          objectsDispatch({
            type: ObjectActionsType.UPDATE_OBJECT_AT_TIME_FROM_MOVEMENT,
            payload: {
              time: tl.scrubbingCurrentTime,
              x: left,
              y: top,
              objectId,
            },
          });
        });
      }}
      onClickGroup={(e) => {
        const selectedTarget = e.inputTarget;
        let elementWithObjectId: Element | null = selectedTarget;
        const maxIterations = 9;
        let selectedObjectId;
        selectedObjectId = getTargetObjectId(elementWithObjectId);
        elementWithObjectId = elementWithObjectId.parentElement!;

        for (let i = 0; i < maxIterations; i++) {
          if (!selectedObjectId && elementWithObjectId) {
            selectedObjectId = getTargetObjectId(elementWithObjectId);
            elementWithObjectId = elementWithObjectId.parentElement!;
          } else {
            break;
          }
        }
        if (!selectedObjectId) return;
        let type = ObjectActionsType.SET_SELECTED_OBJECT;
        if (e.inputEvent.ctrlKey) {
          type = ObjectActionsType.ADD_SELECTED_OBJECT;
        }
        objectsDispatch({
          type,
          payload: { objectId: selectedObjectId },
        });
      }}
      onClipStart={({ inputEvent }) => {
        inputEvent.stopPropagation();
        setLocalKeepAspectRatio(false);
      }}
      onClip={({ target, clipStyles, clipStyle, datas }) => {
        datas.clipStyle = clipStyle;
        target.style.clipPath = clipStyle;
        datas.top = Number.parseFloat(clipStyles[0]);
        datas.right = Number.parseFloat(clipStyles[1]);
        datas.bottom = Number.parseFloat(clipStyles[2]);
        datas.left = Number.parseFloat(clipStyles[3]);
      }}
      onClipEnd={({ datas, target }) => {
        setLocalKeepAspectRatio(null);
        const pixelWidth = framedWidth;
        const pixelHeight = framedHeight;
        const { top, right, bottom, left } = datas;
        // Calculate the width and height of the clipped area
        const clipWidth = pixelWidth - left - right;
        const clipHeight = pixelHeight - top - bottom;

        // Calculate the center of the clipped area
        const clipCenterX = left + clipWidth / 2;
        const clipCenterY = top + clipHeight / 2;

        const percentTop = (top / pixelHeight) * 100;
        const percentRight = (right / pixelWidth) * 100;
        const percentBottom = (bottom / pixelHeight) * 100;
        const percentLeft = (left / pixelWidth) * 100;
        const clipObject = {
          top: percentTop,
          right: percentRight,
          bottom: percentBottom,
          left: percentLeft,
        };

        const percentClipString = `inset(${percentTop}% ${percentRight}% ${percentBottom}% ${percentLeft}%)`;

        objectsDispatch({
          type: ObjectActionsType.SET_OBJECT_CLIP_PATH,
          payload: {
            clipPath: clipObject,
            clipPathString: percentClipString,
            objectId: objectsState.selectedObjects[0].objectId,
          },
        });

        const newTransformOrigin = {
          x: (clipCenterX / pixelWidth) * 100,
          y: (clipCenterY / pixelHeight) * 100,
        };
        const prevRect = target.getBoundingClientRect();
        target.style.transformOrigin = `${newTransformOrigin.x}% ${newTransformOrigin.y}%`;
        const newRect = target.getBoundingClientRect();
        const xDifference = prevRect.x - newRect.x;
        const yDifference = prevRect.y - newRect.y;

        objectsDispatch({
          type: ObjectActionsType.UPDATE_OBJECT,
          payload: {
            objectId: objectsState.selectedObjects[0].objectId,
            object: {
              transformOrigin: newTransformOrigin,
            },
          },
        });

        const pixelX = framedX + xDifference;

        const pixelY = framedY + yDifference;
        objectsDispatch({
          type: ObjectActionsType.UPDATE_OBJECT_AT_TIME_FROM_MOVEMENT,
          payload: {
            objectId: objectsState.selectedObjects[0].objectId,
            time: tl.scrubbingCurrentTime,
            x: pixelX,
            y: pixelY,
          },
        });

        // Apply the updated transform origin and shift to the outer div

        target.style.clipPath = percentClipString;
      }}
      onSnap={({ guidelines, elements, gaps }) => {
        // Store snap data for use in onResizeEnd
        if (isMaskingShape && (guidelines?.length || elements?.length || gaps?.length)) {
          lastSnapDataRef.current = { guidelines, elements, gaps };
        }
      }}
    />
  );
}
